/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Colours and value formatting for report charts.
 *
 * Built from src/styles/tokens/colors.js rather than from fresh hex literals,
 * so a chart sits in the same palette as the rest of the app and a brand
 * change reaches it. The Material Dashboard chart wrappers in src/examples
 * take a single `color` name and generate their own gradient, which is fine
 * for a fixed dashboard tile but cannot colour a series per category — which
 * is exactly what a grouped report needs.
 */

import colors from 'styles/tokens/colors';

/**
 * The categorical sequence.
 *
 * Ordered so that adjacent entries stay distinguishable: hue first, then
 * lightness. A chart of four stages should not be four blues.
 */
export const CATEGORICAL = [
  colors.brand.primary,   // blue
  '#1F8A56',              // green   (colors.success.main)
  '#E68A06',              // amber
  '#7B4FD1',              // violet
  '#0E9BA8',              // teal
  '#BA1A1A',              // red     (colors.error.text)
  '#C2185B',              // magenta
  '#5A6B8C',              // slate
];

/**
 * Status words get their own colour wherever they appear, so "Closed Lost" is
 * the same red on every chart in the product. Matched case-insensitively
 * because the same concept is spelled differently across modules ("Closed
 * Won", "Paid", "Completed" all mean good).
 */
const SEMANTIC = [
  [/(closed won|won|paid|completed|approved|active|qualified|resolved|accepted)/i,
    colors.status.success.dot],
  [/(closed lost|lost|failed|cancelled|canceled|rejected|disqualified|overdue|critical)/i,
    colors.status.error.dot],
  [/(pending|partial|in progress|warning|draft|on hold|high)/i,
    colors.status.warning.dot],
  [/(new|open|sent|secured|medium)/i, colors.status.info.dot],
];

export function colorFor(label, index) {
  const text = String(label ?? '');
  for (const [pattern, color] of SEMANTIC) {
    if (pattern.test(text)) return color;
  }
  return CATEGORICAL[index % CATEGORICAL.length];
}

export function paletteFor(labels) {
  return labels.map((label, index) => colorFor(label, index));
}

/** Add transparency to a hex colour, for area fills and hover states. */
export function alpha(hex, amount) {
  const value = String(hex).replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${amount})`;
}

/**
 * Compact number formatting for axes and KPI tiles.
 *
 * 1.2M rather than 1,200,000: an axis tick that wraps is worse than one that
 * abbreviates, and a KPI tile has one line to work with.
 */
export function compact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * Format one cell according to the column descriptor the backend sent.
 *
 * The descriptor is why this needs no schema knowledge: the engine says a
 * column is currency and this renders it as currency, for a report nobody
 * wrote a component for.
 */
export function formatValue(value, column, currency) {
  if (value === null || value === undefined || value === '') return '—';
  const type = column?.type;

  if (type === 'currency') {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
      }).format(n);
    } catch {
      // An unknown ISO code must not blank the cell.
      return `${currency || ''} ${n.toLocaleString()}`.trim();
    }
  }

  if (type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : String(value);
  }

  if (type === 'date' || type === 'datetime') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    // A date bucket carries no time worth showing, and a datetime column
    // grouped by month is a date.
    return type === 'date' || column?.bucket
      ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
      : d.toLocaleString(undefined, {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
  }

  if (type === 'boolean') return value ? 'Yes' : 'No';

  return String(value);
}

/**
 * Format a cell, preferring a resolved name for a reference column.
 *
 * `labels` comes from the run result — the engine resolves ids to names
 * server-side, so that a chart grouped by owner_id reads "Annie Shorya" rather
 * than 17, and so the names obey the same record scoping as the report.
 *
 * An id with no resolved name falls back to `Trip #17` (using the column
 * label), not a bare `#17`. A null foreign key is `No Trip`, not the vague
 * "Unassigned" that left Booking Value by Trip unexplained.
 */
export function displayValue(value, column, currency, labels) {
  if (column?.type === 'reference') {
    const kind = (column.label || column.reference_to || 'Record')
      .replace(/\s+ID$/i, '')
      .trim() || 'Record';
    if (value === null || value === undefined || value === '') {
      return `No ${kind}`;
    }
    const resolved = labels?.[column.key]?.[String(value)];
    if (resolved) return resolved;
    return `${kind} #${value}`;
  }
  return formatValue(value, column, currency);
}
